import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import MenuIcon from '@mui/icons-material/Menu'
import TranslateIcon from '@mui/icons-material/Translate'
import { Box } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import UserMenu from 'components/UserMenu/UserMenu'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { Colors } from 'theme/colors'
import { DRAWER_WIDTH } from 'theme/constants'
import { useAppState } from '../../state/store'
import { useNextAppDrawerState } from '../AppDrawer/AppDrawer.restate'
import ChangeUserRole from '../ChangeUserRole/ChangeUserRole'
import TranslationMenu from '../TranslationMenu/TranslationMenu'
import messages from './NavBar.messages'

const useClasses = makeStyles(theme => ({
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    fontSize: '150%',
  },
  appBar: {
    backgroundColor: Colors.neutrals[9],
    color: Colors.neutrals[2],
  },
  drawerSpacer: {
    [theme.breakpoints.up('md')]: {
      width: DRAWER_WIDTH,
    },
  },
}))

export interface UserMenuButtonProps {
  open: boolean
  handleClose: () => void
  handleOpen: () => void
  anchorEl?: HTMLDivElement | null
  showTranslationSwitch?: boolean
}

export const TranslationButton: React.FC<UserMenuButtonProps> = ({
  showTranslationSwitch = true,
  handleOpen,
  handleClose,
  open,
}) => {
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const embeddedMode = useAppState(state => state.routerExtension.embeddedMode)

  // Language will be set from the native frontend.
  if (embeddedMode) return null
  if (!showTranslationSwitch) return null

  return (
    <div>
      <div ref={anchorRef}></div>
      <TranslationMenu
        open={open}
        handleClose={handleClose}
        handleOpen={handleOpen}
        anchorEl={anchorRef.current}
      />
      <IconButton
        onClick={() => handleOpen()}
        style={{
          color: Colors.neutrals[2],
          height: '1.5rem',
          width: '1.5rem',
          padding: '1.5rem',
        }}
        size='large'
      >
        <TranslateIcon />
      </IconButton>
    </div>
  )
}

const UserMenuButton: React.FC<UserMenuButtonProps> = ({
  open,
  handleClose,
  handleOpen,
}) => (
  <>
    <UserMenu open={open} handleClose={handleClose} />
    <IconButton onClick={handleOpen} color='inherit' size='large'>
      <AccountCircleIcon />
    </IconButton>
  </>
)

interface NavBarProps {
  title: string
  showBackButton: boolean
}
export const NavBar: React.FC<NavBarProps> = ({ title, showBackButton }) => {
  const intl = useIntl()
  const menuLabel = intl.formatMessage(messages.menuLabel)

  const classes = useClasses()
  const [openUserMenu, setOpenUserMenu] = useState(false)
  const [openTranslationMenu, setOpenTranslationMenu] = useState(false)
  const nextAppDrawer = useNextAppDrawerState()
  const history = useHistory()

  const openDrawer = () =>
    nextAppDrawer(state => {
      state.open = true
    })

  const goBack = () => {
    history.goBack()
  }

  return (
    <div>
      <AppBar position='static' color='inherit' elevation={0}>
        <Toolbar className={classes.appBar}>
          <div className={classes.drawerSpacer} />

          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <IconButton
              edge='start'
              className={classes.menuButton}
              color='inherit'
              aria-label={menuLabel}
              onClick={openDrawer}
              size='large'
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {showBackButton && (
            <IconButton onClick={goBack} size='large'>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant='h6'
            className={classes.title}
            noWrap={true}
            align='center'
          >
            {title}
          </Typography>

          <ChangeUserRole />

          <TranslationButton
            open={openTranslationMenu}
            handleClose={() => setOpenTranslationMenu(false)}
            handleOpen={() => setOpenTranslationMenu(true)}
          />
          <UserMenuButton
            open={openUserMenu}
            handleClose={() => setOpenUserMenu(false)}
            handleOpen={() => setOpenUserMenu(true)}
          />
        </Toolbar>
      </AppBar>
    </div>
  )
}
